import { SyntheticEvent } from 'react';

import { useDirectoryGetRecords } from 'api/requests';
import { convertDirectoryFieldValueToLabel } from 'helpers';
import { useSearchDebounce } from 'hooks/useSearchDebounce';
import {
  AutocompleteField,
  AutocompleteFieldProps,
} from 'libs/form/fields/AutocompleteField';
import { useTranslation } from 'libs/i18n';

interface DirectoryAutocompleteFieldProps<
  Value extends object,
  Multiple extends boolean | undefined
> extends Omit<
    AutocompleteFieldProps<Value, Multiple>,
    'options' | 'optionValueKey' | 'optionLabelKey'
  > {
  directorySchemaName: string;
  fieldName: string;
  directoryRecordFieldFilters?: any;
}

export const DirectoryAutocompleteField = <
  Value extends object,
  Multiple extends boolean | undefined
>({
  directorySchemaName,
  fieldName,
  directoryRecordFieldFilters,
  ...autocompleteFieldProps
}: DirectoryAutocompleteFieldProps<Value, Multiple>) => {
  const { t, language } = useTranslation();

  const { search, debouncedFunc } = useSearchDebounce();

  const { listData, loading } = useDirectoryGetRecords({
    pagination: { limit: 20, page: 0 },
    filter: {
      search,
    },
    params: {
      directorySchemaName,
      onlyIsActive: true,
      fields: directoryRecordFieldFilters
        ? JSON.stringify(directoryRecordFieldFilters)
        : undefined,
    },
  });

  const onInputChange = (_: SyntheticEvent, v: string) => {
    debouncedFunc(v);
  };

  const onClose = () => {
    debouncedFunc('');
  };

  return (
    <AutocompleteField
      {...autocompleteFieldProps}
      disabledFilterOptions
      loading={loading}
      optionLabelKey="label"
      options={
        (listData?.list ?? []).map((record) => {
          const label = record.fields.find(
            (field) => field.name === fieldName
          )?.value;

          const convertedValueLabel = convertDirectoryFieldValueToLabel({
            t,
            language,
            fieldValue: label,
          });

          return {
            label: convertedValueLabel,
            value: record.id,
            record,
          };
        }) as Value[]
      }
      optionValueKey="value"
      onClose={onClose}
      onInputChange={onInputChange}
    />
  );
};
